import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Lock, Check, ChevronRight, Footprints, BookOpen, Dumbbell, ClipboardCheck, Trophy, X } from "lucide-react";
import LeanDashboard from "@/pages/portal/LeanDashboard";
import BuildDashboard from "@/pages/portal/BuildDashboard";
import CurriculumVideoCard from "@/components/curriculum/CurriculumVideoCard";
import { RebuildScoreCard } from "@/components/RebuildScoreCard";
import { ChoosePathway, PathwayCountdown } from "@/components/ChoosePathway";
import { LifestyleStrip, ReviewDueCard, LessonHero } from "@/components/LongevityCards";

const PHASE_STYLE = {
  RESET:     { bg: "bg-primary",       text: "text-primary-foreground", accent: "text-primary" },
  ESTABLISH: { bg: "bg-blue-600",      text: "text-white",              accent: "text-blue-600" },
  PROGRESS:  { bg: "bg-emerald-600",   text: "text-white",              accent: "text-emerald-600" },
  "OWN IT":  { bg: "bg-[#7C3AED]",     text: "text-white",              accent: "text-[#7C3AED]" },
};

function WalkCard({ walk, week, refresh }) {
  const [showUnlock, setShowUnlock] = useState(false);
  const [editBaseline, setEditBaseline] = useState(false);
  const [baselineInput, setBaselineInput] = useState(walk.baseline_steps || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!walk.unlock) return;
    const key = `walk-unlock-seen-w${week}`;
    if (!localStorage.getItem(key)) setShowUnlock(true);
  }, [walk.unlock, week]);

  const dismissUnlock = () => {
    localStorage.setItem(`walk-unlock-seen-w${week}`, "1");
    setShowUnlock(false);
  };

  const saveBaseline = async () => {
    setBusy(true);
    try {
      await api.patch("/user/baseline", { baseline_steps: parseInt(baselineInput || 0, 10) });
      toast.success("Baseline saved");
      setEditBaseline(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const phase = walk.phase;
  const pct = walk.today_progress_pct;
  const baselineBumped = walk.personal_target > walk.phase_target;
  const journey = walk.journey || [];

  return (
    <div className="space-y-4" data-testid="walk-card">
      {/* Unlock banner (Weeks 4/7/10) */}
      {showUnlock && walk.unlock && (
        <div className="rounded-lg p-5 flex items-start justify-between gap-3"
          style={{ backgroundColor: walk.colour, color: "#fff" }}
          data-testid="walk-unlock-banner">
          <div>
            <div className="label-eyebrow !text-white/80">Phase unlock</div>
            <div className="mt-1 font-head uppercase text-lg md:text-xl">{walk.unlock.title}</div>
            <div className="mt-2 text-sm text-white/90 max-w-2xl">{walk.unlock.body}</div>
          </div>
          <button onClick={dismissUnlock} className="text-white/80 hover:text-white shrink-0" data-testid="walk-unlock-dismiss">
            <X size={18} />
          </button>
        </div>
      )}

      {/* WALK card */}
      <div className="bg-card border border-border rounded-lg p-7" data-testid="walk-card-body">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <Footprints size={14} strokeWidth={2.5} /> Walk · {phase}
            </div>
            <div className="mt-2 font-head uppercase text-xl md:text-2xl leading-tight">{walk.headline}</div>
            <div className="mt-1 text-xs text-muted-foreground max-w-xl">{walk.message}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Daily target</div>
            <div className="wordmark text-4xl md:text-5xl leading-none" style={{ color: walk.colour }}>
              {(walk.personal_target || 0).toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
              {baselineBumped ? "Baseline-protected" : `${(walk.phase_target || 0).toLocaleString()} phase target`}
            </div>
          </div>
        </div>

        {/* Today's progress bar */}
        <div className="mt-5">
          <div className="flex justify-between text-[11px] uppercase tracking-widest text-muted-foreground">
            <span>Today · {(walk.today_steps || 0).toLocaleString()} / {(walk.personal_target || 0).toLocaleString()}</span>
            <span>{pct}%</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: walk.colour }}
              data-testid="walk-today-bar" />
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            7-day: {walk.avg_steps_7d.toLocaleString()} avg · {walk.days_hit_7d}/{walk.logged_days_7d} days hit target
          </div>
        </div>

        {/* Journey line */}
        <div className="mt-6">
          <div className="label-eyebrow">Walking journey</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="walk-journey">
            {journey.map((j, i) => {
              const isCurrent = j.phase === phase;
              const isPast = journey.findIndex((x) => x.phase === phase) > i;
              return (
                <div key={j.phase} className="flex items-center gap-2">
                  <div className={`px-3 py-2 rounded border text-center ${isCurrent ? "border-transparent text-white" : isPast ? "border-border text-foreground bg-muted" : "border-border text-muted-foreground"}`}
                    style={isCurrent ? { backgroundColor: walk.colour, borderColor: walk.colour } : {}}
                    data-testid={`walk-journey-${j.phase.toLowerCase().replace(" ", "-")}`}>
                    <div className="wordmark text-lg leading-none">{(j.target / 1000)}K+</div>
                    <div className="text-[9px] uppercase tracking-widest opacity-80 mt-1">{j.phase} · W{j.weeks}</div>
                  </div>
                  {i < journey.length - 1 && <ChevronRight size={14} className="text-muted-foreground" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Baseline + CTA */}
        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          {editBaseline ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Your baseline</span>
              <input type="number" min="0" value={baselineInput}
                onChange={(e) => setBaselineInput(e.target.value)}
                className="bg-background border border-border rounded px-3 py-1.5 text-sm w-32"
                data-testid="walk-baseline-input" />
              <button onClick={saveBaseline} disabled={busy}
                className="bg-foreground text-background text-[11px] uppercase tracking-widest px-3 py-1.5 rounded disabled:opacity-60"
                data-testid="walk-baseline-save">Save</button>
              <button onClick={() => setEditBaseline(false)}
                className="text-[11px] uppercase tracking-widest text-muted-foreground px-2">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setEditBaseline(true)}
              className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
              data-testid="walk-baseline-edit">
              Baseline: {(walk.baseline_steps || 0).toLocaleString()} steps · edit
            </button>
          )}
          <Link to="/app/track"
            className="bg-foreground text-background rounded px-4 py-2 text-[11px] uppercase tracking-widest hover:opacity-90"
            data-testid="walk-log-cta">
            {walk.today_steps > 0 ? "Update Steps" : "Log Steps"} →
          </Link>
        </div>
      </div>
    </div>
  );
}

function EatCard({ eat, week, refresh }) {
  const [showUnlock, setShowUnlock] = useState(false);
  useEffect(() => {
    if (!eat.unlock) return;
    const key = `eat-unlock-seen-w${week}`;
    if (!localStorage.getItem(key)) setShowUnlock(true);
  }, [eat.unlock, week]);
  const dismiss = () => { localStorage.setItem(`eat-unlock-seen-w${week}`, "1"); setShowUnlock(false); };

  const statusLabel = eat.today_status === "achieved" ? "Achieved" : eat.today_status === "missed" ? "Missed" : "Not logged";
  const statusTone = eat.today_status === "achieved" ? "bg-emerald-600 text-white"
                    : eat.today_status === "missed" ? "bg-destructive text-destructive-foreground"
                    : "bg-muted text-muted-foreground";

  return (
    <div className="space-y-4" data-testid="eat-card">
      {showUnlock && eat.unlock && (
        <div className="rounded-lg p-5 flex items-start justify-between gap-3"
          style={{ backgroundColor: eat.colour, color: "#fff" }}
          data-testid="eat-unlock-banner">
          <div>
            <div className="label-eyebrow !text-white/80">EAT phase unlock</div>
            <div className="mt-1 font-head uppercase text-lg md:text-xl">{eat.unlock.title}</div>
            <div className="mt-2 text-sm text-white/90 max-w-2xl">{eat.unlock.body}</div>
          </div>
          <button onClick={dismiss} className="text-white/80 hover:text-white shrink-0" data-testid="eat-unlock-dismiss">
            <X size={18} />
          </button>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg p-7" data-testid="eat-card-body">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow flex items-center gap-2">EAT · {eat.phase}</div>
            <div className="mt-2 font-head uppercase text-xl md:text-2xl leading-tight">{eat.headline}</div>
            <div className="mt-1 text-xs text-muted-foreground max-w-xl">{eat.message}</div>
          </div>
          <div className="text-right shrink-0">
            <span className={`inline-block text-[10px] uppercase tracking-widest px-3 py-1.5 rounded ${statusTone}`}
              data-testid="eat-today-status">{statusLabel}</span>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Today</div>
          </div>
        </div>
        <div className="mt-5 rounded-md border border-border p-4 bg-muted/40">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Today's standard</div>
          <div className="mt-1 text-sm">{eat.standard}</div>
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
          7-day EAT: {eat.days_hit_7d}/{eat.days_evaluated_7d} days achieved
        </div>
        <div className="mt-6">
          <div className="label-eyebrow">EAT journey</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="eat-journey">
            {(eat.journey || []).map((j, i) => {
              const isCurrent = j.phase === eat.phase;
              const idx = (eat.journey || []).findIndex((x) => x.phase === eat.phase);
              const isPast = idx > i;
              return (
                <div key={j.phase} className="flex items-center gap-2">
                  <div className={`px-3 py-2 rounded border text-center ${isCurrent ? "border-transparent text-white" : isPast ? "border-border text-foreground bg-muted" : "border-border text-muted-foreground"}`}
                    style={isCurrent ? { backgroundColor: eat.colour, borderColor: eat.colour } : {}}
                    data-testid={`eat-journey-${j.phase.toLowerCase().replace(" ", "-")}`}>
                    <div className="text-[11px] uppercase tracking-widest leading-tight">{j.label}</div>
                    <div className="text-[9px] uppercase tracking-widest opacity-80 mt-1">{j.phase} · W{j.weeks}</div>
                  </div>
                  {i < (eat.journey || []).length - 1 && <ChevronRight size={14} className="text-muted-foreground" />}
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Link to="/app/track"
            className="bg-foreground text-background rounded px-4 py-2 text-[11px] uppercase tracking-widest hover:opacity-90"
            data-testid="eat-log-cta">Log today's eating →</Link>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [j, setJ] = useState(null);
  const [pathway, setPathway] = useState(undefined);

  const load = async () => {
    try {
      const [dash, journey, pw] = await Promise.all([api.get("/dashboard"), api.get("/journey"), api.get("/pathway/state")]);
      setD(dash.data); setJ(journey.data); setPathway(pw.data.active_pathway);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Active pathway routing — LEAN / BUILD take over the /app dashboard
  if (pathway === "LEAN") return <LeanDashboard />;
  if (pathway === "BUILD") return <BuildDashboard />;

  if (!d || !j) return <div className="text-muted-foreground uppercase tracking-wider text-sm">Loading your rebuild…</div>;

  const cur = j.weeks.find((w) => w.week === j.current_week) || j.weeks[0];
  const phaseS = PHASE_STYLE[j.current_phase] || PHASE_STYLE.RESET;

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* Longevity Foundation header */}
      <div className={`${phaseS.bg} ${phaseS.text} rounded-lg p-8 md:p-10`} data-testid="phase-header">
        <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Your Longevity Foundation</div>
        <div className="flex items-end justify-between gap-6 flex-wrap mt-3">
          <div>
            <h1 className="wordmark text-4xl md:text-6xl uppercase leading-[0.9]" data-testid="dashboard-week-title">Week {j.current_week} of 12</h1>
            <div className="mt-2 font-display uppercase text-sm tracking-widest opacity-90" data-testid="dashboard-phase-label">Stage · {d.phase_label || j.current_phase_label || j.current_phase}</div>
            <p className="mt-3 max-w-xl opacity-90 text-sm">Build healthy habits today for a more capable tomorrow.</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest opacity-70">Weeks {(j.phase_meta[j.current_phase]?.weeks || []).join('–')}</div>
            <div className="wordmark text-6xl md:text-8xl leading-none">{j.current_week}<span className="opacity-50 text-3xl">/12</span></div>
          </div>
        </div>
      </div>

      {d.reset_complete
        ? (d.active_pathway !== "LEAN" && d.active_pathway !== "BUILD") && <ChoosePathway chosen={d.chosen_pathway} refresh={load} />
        : <PathwayCountdown days={d.days_until_reset_complete} />}

      {d.lesson && <LessonHero week={d.week} lesson={d.lesson} phaseLabel={d.phase_label} />}
      <ReviewDueCard stage={d.review_due} />

      {/* WEEKLY LESSON — 10K30 Curriculum Video Placeholder (per approved brief) */}
      <CurriculumVideoCard />

      {/* WALK — phase-aware target progression (BA30 10K30 brief) */}
      {d.walk && <WalkCard walk={d.walk} week={d.week} refresh={load} />}

      {/* EAT — phase-aware nutrition progression (BA30 10K30 EAT brief) */}
      {d.eat && <EatCard eat={d.eat} week={d.week} refresh={load} />}

      {/* RECOVER / CONNECT — lightweight prompts, never compulsory */}
      <LifestyleStrip prompts={d.lifestyle_prompts} refresh={load} />

      {/* Missing logs soft nudge — Retrospective Logging brief */}
      {d.unlogged_days_count > 0 && (
        <a href="/app/track" className="block bg-card border border-primary/40 rounded-lg p-5 hover:bg-primary/5 transition-colors" data-testid="missing-logs-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="label-eyebrow">Pick up where you left off</div>
              <div className="mt-1 font-head uppercase text-lg">
                {d.unlogged_days_count} day{d.unlogged_days_count === 1 ? "" : "s"} can still be logged
              </div>
              <div className="mt-1 text-xs text-muted-foreground">No data ≠ a failed day. Backfill any past day whenever suits you — no penalties, no broken streaks.</div>
            </div>
            <span className="text-xs uppercase tracking-widest text-primary shrink-0">Review missing days →</span>
          </div>
        </a>
      )}

      {/* Current Week Focus — Master Curriculum module */}
      <Link to={`/app/week/${cur.week}`} className="block bg-foreground text-background rounded-lg p-7 hover:bg-primary transition-colors group" data-testid="current-week-card">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="label-eyebrow !text-background/70">This Week's Module · Week {cur.week}</div>
            <div className="mt-2 font-head text-2xl md:text-3xl leading-snug uppercase">{cur.module_name || cur.focus}</div>
            {cur.train_headline && <div className="mt-3 text-xs uppercase tracking-widest text-primary group-hover:text-primary-foreground/90">TRAIN · {cur.train_headline}</div>}
            <div className="mt-2 text-xs text-background/70">Weekly Lesson: {cur.lesson_title}</div>
          </div>
          <div className="shrink-0 flex items-center gap-2 text-xs uppercase tracking-widest text-primary group-hover:text-primary-foreground">
            Open Week <ChevronRight size={16} />
          </div>
        </div>
      </Link>

      {/* Score + Quick Stats */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <RebuildScoreCard score={d.rebuild_score_v2} />
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-3">
          {[
            { icon: Footprints, label: "Avg steps / day", value: d.stats.avg_steps.toLocaleString() },
            { icon: Dumbbell, label: "Workouts (7d)", value: d.stats.workouts_done },
          ].map((s) => (
            <div key={s.label} className="bg-card border border-border rounded-lg p-5" data-testid={`stat-${s.label.split(' ')[0].toLowerCase()}`}>
              <s.icon className="text-primary" size={18} strokeWidth={2.25} />
              <div className="mt-3 wordmark text-4xl">{s.value}</div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
          <Link to={`/app/week/${cur.week}#workouts`} className="bg-primary text-primary-foreground rounded-lg p-5 lift col-span-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">Next Session</div>
              <div className="mt-1 font-head uppercase text-xl">Workout A · {j.current_phase}</div>
            </div>
            <ChevronRight size={20} />
          </Link>
        </div>
      </div>

      {/* 12-Week Grid */}
      <div>
        <div className="label-eyebrow">Program Progress · 12 Weeks</div>
        <div className="mt-5 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2" data-testid="program-progress">
          {j.weeks.map((w) => {
            const s = PHASE_STYLE[w.phase] || PHASE_STYLE.RESET;
            const state = w.status;
            const isLocked = state === "locked";
            const cls = state === "complete" ? `${s.bg} ${s.text} border-transparent`
              : state === "current" ? `bg-foreground text-background border-foreground ring-2 ring-primary`
              : "bg-muted text-muted-foreground border-border";
            const Wrapper = isLocked ? "div" : Link;
            const props = isLocked ? {} : { to: `/app/week/${w.week}` };
            return (
              <Wrapper key={w.week} {...props}
                className={`aspect-square rounded-md border flex flex-col items-center justify-center relative ${cls} ${!isLocked ? "hover:opacity-90 transition-opacity cursor-pointer" : "cursor-not-allowed"}`}
                data-testid={`week-tile-${w.week}`}>
                {isLocked && <Lock size={12} className="absolute top-1.5 right-1.5 opacity-60" />}
                {state === "complete" && <Check size={12} className="absolute top-1.5 right-1.5" strokeWidth={3} />}
                <span className="opacity-70 text-[9px] uppercase tracking-widest">Wk</span>
                <span className="wordmark text-xl">{w.week}</span>
              </Wrapper>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-[10px] uppercase tracking-widest text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-primary rounded-sm inline-block" />RESET · W1-3</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-600 rounded-sm inline-block" />ESTABLISH · W4-6</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-600 rounded-sm inline-block" />PROGRESS · W7-9</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-[#7C3AED] rounded-sm inline-block" />OWN IT · W10-12</span>
        </div>
      </div>

      {/* Community pulse */}
      <div className="bg-card border border-border rounded-lg p-7">
        <div className="label-eyebrow flex items-center gap-2"><Trophy size={14} strokeWidth={2.5} /> Community Pulse</div>
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          <div><div className="wordmark text-4xl">{d.community_pulse.walks_logged_week}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">walks logged</div></div>
          <div><div className="wordmark text-4xl">{d.community_pulse.workouts_logged_week}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">workouts done</div></div>
          <div><div className="wordmark text-4xl">{d.community_pulse.wins_this_week}</div><div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">wins this week</div></div>
        </div>
      </div>
    </div>
  );
}
